import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  Search, 
  Filter, 
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Award,
  FileText,
  Calendar,
  DollarSign,
  Building2,
  Loader2,
  AlertCircle,
  Plus
} from 'lucide-react'
import apiService from '../../services/api'
import { toast } from 'react-toastify'

interface VendorProposal {
  id: string
  message: string
  status: 'submitted' | 'under-review' | 'accepted' | 'rejected'
  submittedAt: string
  reviewedAt?: string
  reviewNotes?: string
  GovRfq: {
    id: string
    title: string
    description: string
    deadline: string
    budgetRange: string
    GovAgency: {
      name: string
      agencyType: string
    }
  }
  attachments: string[]
}

const VendorProposalsPage: React.FC = () => {
  const [proposals, setProposals] = useState<VendorProposal[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchMyProposals()
  }, [])

  const fetchMyProposals = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.get('/api/gov/proposals/vendor') as any
      
      if (response?.success) {
        setProposals(response.data || [])
      } else {
        setError('Failed to fetch your proposals')
        toast.error('Failed to fetch your proposals')
      }
    } catch (err) {
      console.error('Error fetching my proposals:', err)
      setError('Failed to fetch your proposals')
      toast.error('Failed to fetch your proposals')
    } finally {
      setLoading(false)
    }
  }

  const filteredProposals = proposals.filter(proposal => {
    const matchesSearch = proposal.GovRfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proposal.GovRfq.GovAgency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proposal.message.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = statusFilter === 'all' || proposal.status === statusFilter
    
    return matchesSearch && matchesFilter
  })

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      submitted: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: Clock },
      'under-review': { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: Eye },
      accepted: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: XCircle }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.submitted
    const Icon = config.icon
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    )
  }

  const ProposalCard: React.FC<{ proposal: VendorProposal }> = ({ proposal }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {proposal.GovRfq.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            <Building2 className="w-4 h-4 inline mr-1" />
            {proposal.GovRfq.GovAgency.name} ({proposal.GovRfq.GovAgency.agencyType})
          </p>
        </div>
        
        {getStatusBadge(proposal.status)}
      </div>
      
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
          {proposal.message}
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4 mr-2" />
          Submitted {new Date(proposal.submittedAt).toLocaleDateString()}
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <DollarSign className="w-4 h-4 mr-2" />
          {proposal.GovRfq.budgetRange}
        </div>
        {proposal.attachments.length > 0 && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <FileText className="w-4 h-4 mr-2" />
            {proposal.attachments.length} attachment(s)
          </div>
        )}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Clock className="w-4 h-4 mr-2" />
          RFQ Deadline: {new Date(proposal.GovRfq.deadline).toLocaleDateString()}
        </div>
      </div>
      
      {proposal.reviewNotes && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Agency Review Notes:</strong> {proposal.reviewNotes}
          </p>
        </div>
      )}
      
      <div className="flex items-center justify-between pt-4 border-t dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => window.open(`/gov/proposals/${proposal.id}`, '_blank')}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
          >
            <Eye className="w-4 h-4 inline mr-1" />
            View Details
          </button>
        </div>
        
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {proposal.reviewedAt && `Reviewed ${new Date(proposal.reviewedAt).toLocaleDateString()}`}
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/government"
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                My Proposals
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Track your government contract proposals and their status
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0 flex items-center space-x-4">
              <button
                onClick={fetchMyProposals}
                disabled={loading}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              
              <Link
                to="/gov/rfqs"
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Find RFQs</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search proposals by RFQ title, agency, or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="all">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="under-review">Under Review</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredProposals.length} of {proposals.length} proposals
          </p>
        </div>

        {/* Proposals Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading your proposals...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchMyProposals}
                className="btn btn-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Award className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm || statusFilter !== 'all'
                  ? 'No proposals match your search criteria.' 
                  : 'You haven\'t submitted any proposals yet.'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Link
                  to="/gov/rfqs"
                  className="btn btn-primary"
                >
                  Browse Available RFQs
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredProposals.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default VendorProposalsPage